import * as React from "react";
import { PatientApi } from "../../../api/PatientApi";
import { IPatient } from "../../../models/IPatient";
import { useAuth } from "../../../App";
import { useParams, Switch, useRouteMatch, Route } from "react-router-dom";
import { SpinnerWrapper } from "soprasteria-ui-library";
import { setMessage } from "../../../helpers/MessageHelper";
import { PatientStart } from "./PatientStart";
import { TREATMENT_REPORT, EMERGENCY_TREATMENT, REFERRAL, RISK_POT_REPORT, REFERRAL_MANUAL_REGISTRATION, ORTHODONTIC_REFERRAL, GENERAL_DENTAL_REFERRAL, ORTHODONTIC_LKGCF_PATIENT_EXTENSION } from "../../../utils/RouteConstants";
import { Referral } from "../Referral/Referral";
import { TreatmentReport } from "../TreatmentReport/TreatmentReport";
import { PATIENT_SESSION } from "./PatientConstants";
import { PatientInformation } from "../../PatientInformation/PatientInformation";
import { EmergencyTreatmentReport } from "../EmergencyTreatment/EmergencyTreatmentReport";
import { RiskPotReport } from "../RiskPotReport/RiskPotReport";
import { PatientNotFound } from "./PatientNotFound";
import { ReferralManualRegistration } from "../Referral/ReferralManualRegistration/ReferralManualRegistration";
import { OrthodonticReferral } from "../OrthodonticReferral/OrthodonticReferral";
import { GeneralDentalReferral } from "../GeneralDentalReferral/GeneralDentalReferral";
import { OrthodonticLkgCfPatientExtension } from "../OrthodonticLkgCfPatientExtension/OrthodonticLkgCfPatientExtension";

export const Patient: React.FunctionComponent<{}> = () => {
    const [patient, setPatient] = React.useState<IPatient>();
    const abortController = new AbortController();
    const api = new PatientApi(useAuth());
    let match = useRouteMatch();
    const { patientGuidId } = useParams<{patientGuidId: string}>();
    const [errorMessage, setErrorMessage] = React.useState<string>("");
    const [isPatientLoading, setIsPatientLoading] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (patientGuidId) {
            const patientSession = window.sessionStorage.getItem(PATIENT_SESSION);
            if (patientSession !== null) {
                const patientFromSession = JSON.parse(patientSession) as IPatient;
                if (patientFromSession.patientGuidId === patientGuidId) {
                    setPatient(patientFromSession);
                    return;
                }
            }
            loadPatient();
        }

        return () => {
            abortController.abort();
        }
    }, [patientGuidId])

    const loadPatient = (onCallSuccess?: () => any) => {
        setIsPatientLoading(true);
        if (patientGuidId) {
            api.getPatient(patientGuidId, (data: IPatient) => {
                setPatient(data);
                window.sessionStorage.setItem(PATIENT_SESSION, JSON.stringify(data));
                setErrorMessage("");
                if (onCallSuccess) {
                    onCallSuccess();
                }
            }, (message) => {
                const newMessage = setMessage(message, errorMessage);
                setErrorMessage(newMessage);
                setPatient(undefined);
            }, () => {
                setIsPatientLoading(false);
            });
        }
    }

    return (
        <SpinnerWrapper isLoading={isPatientLoading}>
            <section>
                {
                    !match.isExact ?
                        <div className="row">
                            <div className="col-12 text-right">
                                <PatientInformation patient={patient} />
                            </div>
                        </div>
                        : null
                }
                {
                    patient ?
                        <Switch>
                            <Route exact path={`${match.path}`}>
                                <PatientStart patientGuidId={patientGuidId} />
                            </Route>
                            <Route exact path={`${match.path}/${REFERRAL}/:id?`}>
                                <Referral patient={patient} />
                            </Route>
                            <Route exact path={`${match.path}/${TREATMENT_REPORT}/:id`} >
                                <TreatmentReport />
                            </Route>
                            <Route exact path={`${match.path}/${EMERGENCY_TREATMENT}/:id`} >
                                <EmergencyTreatmentReport patient={patient} />
                            </Route>
                            <Route exact path={`${match.path}/${RISK_POT_REPORT}/:id`} >
                                <RiskPotReport patient={patient} />
                            </Route>
                            <Route exact path={`${match.path}/${REFERRAL_MANUAL_REGISTRATION}`}>
                                <ReferralManualRegistration patient={patient} />
                            </Route>
                            <Route exact path={`${match.path}/${ORTHODONTIC_REFERRAL}/:id?`}>
                                <OrthodonticReferral patient={patient} />
                            </Route>
                            <Route exact path={`${match.path}/${GENERAL_DENTAL_REFERRAL}/:id?`}>
                                <GeneralDentalReferral patient={patient} />
                            </Route>
                            <Route exact path={`${match.path}/${ORTHODONTIC_LKGCF_PATIENT_EXTENSION}/:id?`}>
                                <OrthodonticLkgCfPatientExtension patient={patient} />
                            </Route>
                        </Switch>
                        : <PatientNotFound errorMessage={errorMessage} />
                }
            </section>
        </SpinnerWrapper>
    )
}